import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  CloudSync as CloudSyncIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  MonitorHeart as MonitorIcon,
} from '@mui/icons-material'

const SystemConfiguration = ({ settings = {}, onChange, isLoading = false }) => {
  const [localSettings, setLocalSettings] = useState({
    // System Performance
    maxConcurrentConnections: 1000,
    requestTimeout: 30,
    keepAliveTimeout: 5,
    maxRequestSize: 10,
    enableGzipCompression: true,
    
    // Database Settings
    dbConnectionPoolSize: 20,
    dbConnectionTimeout: 30,
    dbQueryTimeout: 10,
    enableDbCaching: true,
    dbCacheTtl: 300,
    
    // Memory Management
    nodeMaxMemory: 4096,
    gcHeapThreshold: 80,
    enableMemoryMonitoring: true,
    memoryCleanupInterval: 3600,
    
    // File Storage
    maxFileUploadSize: 50,
    allowedFileTypes: 'jpg,jpeg,png,pdf,doc,docx',
    storageCleanupDays: 30,
    enableFileCompression: true,
    
    // Backup Configuration
    enableAutoBackup: true,
    backupFrequency: 'daily',
    backupRetentionDays: 30,
    backupLocation: '/var/backups/virtualsms',
    
    // Logging Configuration
    logLevel: 'info',
    maxLogFileSize: 100,
    logRotationDays: 7,
    enableRequestLogging: true,
    enableErrorReporting: true,
    
    // Maintenance
    enableMaintenanceMode: false,
    maintenanceMessage: 'Sistem bakımda. Lütfen daha sonra tekrar deneyin.',
    autoRestartOnCrash: true,
    restartMaxAttempts: 3,
    
    // Health Monitoring
    enableHealthChecks: true,
    healthCheckInterval: 60,
    alertThresholds: {
      cpu: 80,
      memory: 85,
      disk: 90,
      responseTime: 2000
    },
    
    ...settings
  })

  const [systemStats] = useState({
    cpu: 45,
    memory: 67,
    disk: 34,
    uptime: '5 gün 12 saat',
    connections: 234,
    requestsPerMinute: 1250
  })

  const [services] = useState([
    { name: 'API Server', status: 'running', uptime: '5d 12h', cpu: 15, memory: 512 },
    { name: 'Database', status: 'running', uptime: '5d 12h', cpu: 25, memory: 1024 },
    { name: 'Redis Cache', status: 'running', uptime: '5d 12h', cpu: 5, memory: 256 },
    { name: 'SMS Workers', status: 'running', uptime: '5d 12h', cpu: 8, memory: 128 },
    { name: 'Backup Service', status: 'stopped', uptime: '0h', cpu: 0, memory: 0 }
  ])

  const [cleanupDialog, setCleanupDialog] = useState({ open: false, type: null })

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }))
    onChange?.()
  }

  const handleNestedSettingChange = (parent, key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: value
      }
    }))
    onChange?.()
  }

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'success'
      case 'stopped': return 'error'
      case 'warning': return 'warning'
      default: return 'default'
    }
  }

  const handleCleanup = (type) => {
    setCleanupDialog({ open: true, type })
  }

  const performCleanup = () => {
    // Cleanup logic would go here
    setCleanupDialog({ open: false, type: null })
    // toast.success('Temizlik işlemi başlatıldı')
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Sistem Konfigürasyonu
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sistem performansı ve altyapı ayarları
        </Typography>
      </Box>

      {/* System Status Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonitorIcon />
            Sistem Durumu
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                  {systemStats.cpu}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  CPU Kullanımı
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={systemStats.cpu} 
                  color={systemStats.cpu > 80 ? 'error' : 'primary'}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
                  {systemStats.memory}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  RAM Kullanımı
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={systemStats.memory} 
                  color={systemStats.memory > 85 ? 'error' : 'info'}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                  {systemStats.disk}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Disk Kullanımı
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={systemStats.disk} 
                  color={systemStats.disk > 90 ? 'error' : 'success'}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                  {systemStats.connections}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Aktif Bağlantı
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 700 }}>
                  {systemStats.requestsPerMinute}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  İstek/Dakika
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                  {systemStats.uptime}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Çalışma Süresi
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Services Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Servis Durumu
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Servis</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Çalışma Süresi</TableCell>
                  <TableCell>CPU</TableCell>
                  <TableCell>RAM</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((service, index) => (
                  <TableRow key={index}>
                    <TableCell>{service.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={service.status === 'running' ? 'Çalışıyor' : 'Durdu'}
                        color={getStatusColor(service.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{service.uptime}</TableCell>
                    <TableCell>{service.cpu}%</TableCell>
                    <TableCell>{formatBytes(service.memory * 1024 * 1024)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary">
                        <RefreshIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Performance Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon />
            Performans Ayarları
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maksimum Eşzamanlı Bağlantı"
                type="number"
                value={localSettings.maxConcurrentConnections}
                onChange={(e) => handleSettingChange('maxConcurrentConnections', parseInt(e.target.value) || 1000)}
                helperText="Sunucunun aynı anda kabul edeceği maksimum bağlantı sayısı"
                inputProps={{ min: 100, max: 10000 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="İstek Zaman Aşımı (saniye)"
                type="number"
                value={localSettings.requestTimeout}
                onChange={(e) => handleSettingChange('requestTimeout', parseInt(e.target.value) || 30)}
                helperText="HTTP istekleri için zaman aşımı süresi"
                inputProps={{ min: 5, max: 300 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Keep-Alive Timeout (saniye)"
                type="number"
                value={localSettings.keepAliveTimeout}
                onChange={(e) => handleSettingChange('keepAliveTimeout', parseInt(e.target.value) || 5)}
                helperText="HTTP bağlantılarının açık tutulma süresi"
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maksimum İstek Boyutu (MB)"
                type="number"
                value={localSettings.maxRequestSize}
                onChange={(e) => handleSettingChange('maxRequestSize', parseInt(e.target.value) || 10)}
                helperText="Kabul edilecek maksimum HTTP istek boyutu"
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.enableGzipCompression}
                    onChange={(e) => handleSettingChange('enableGzipCompression', e.target.checked)}
                  />
                }
                label="GZIP Sıkıştırma (Yanıt boyutunu küçültmek için)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Database Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon />
            Veritabanı Ayarları
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Bağlantı Havuzu Boyutu"
                type="number"
                value={localSettings.dbConnectionPoolSize}
                onChange={(e) => handleSettingChange('dbConnectionPoolSize', parseInt(e.target.value) || 20)}
                helperText="Maksimum veritabanı bağlantı sayısı"
                inputProps={{ min: 5, max: 200 }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Bağlantı Timeout (saniye)"
                type="number"
                value={localSettings.dbConnectionTimeout}
                onChange={(e) => handleSettingChange('dbConnectionTimeout', parseInt(e.target.value) || 30)}
                helperText="Veritabanı bağlantısı zaman aşımı"
                inputProps={{ min: 5, max: 300 }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Sorgu Timeout (saniye)"
                type="number"
                value={localSettings.dbQueryTimeout}
                onChange={(e) => handleSettingChange('dbQueryTimeout', parseInt(e.target.value) || 10)}
                helperText="SQL sorguları için zaman aşımı"
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.enableDbCaching}
                    onChange={(e) => handleSettingChange('enableDbCaching', e.target.checked)}
                  />
                }
                label="Veritabanı Cache'i Etkinleştir"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cache TTL (saniye)"
                type="number"
                value={localSettings.dbCacheTtl}
                onChange={(e) => handleSettingChange('dbCacheTtl', parseInt(e.target.value) || 300)}
                disabled={!localSettings.enableDbCaching}
                helperText="Cache'te veri tutma süresi"
                inputProps={{ min: 60, max: 3600 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Memory Management */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MemoryIcon />
            Bellek Yönetimi
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Node.js Max Memory (MB)"
                type="number"
                value={localSettings.nodeMaxMemory}
                onChange={(e) => handleSettingChange('nodeMaxMemory', parseInt(e.target.value) || 4096)}
                helperText="Node.js prosesi için maksimum bellek kullanımı"
                inputProps={{ min: 512, max: 16384 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="GC Heap Eşiği (%)"
                type="number"
                value={localSettings.gcHeapThreshold}
                onChange={(e) => handleSettingChange('gcHeapThreshold', parseInt(e.target.value) || 80)}
                helperText="Garbage Collection tetikleme eşiği"
                inputProps={{ min: 50, max: 95 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.enableMemoryMonitoring}
                    onChange={(e) => handleSettingChange('enableMemoryMonitoring', e.target.checked)}
                  />
                }
                label="Bellek İzleme Etkinleştir"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bellek Temizlik Aralığı (saniye)"
                type="number"
                value={localSettings.memoryCleanupInterval}
                onChange={(e) => handleSettingChange('memoryCleanupInterval', parseInt(e.target.value) || 3600)}
                helperText="Otomatik bellek temizliği aralığı"
                inputProps={{ min: 300, max: 86400 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* File Storage */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Dosya Depolama
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maksimum Dosya Boyutu (MB)"
                type="number"
                value={localSettings.maxFileUploadSize}
                onChange={(e) => handleSettingChange('maxFileUploadSize', parseInt(e.target.value) || 50)}
                helperText="Yüklenebilecek maksimum dosya boyutu"
                inputProps={{ min: 1, max: 1000 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dosya Temizlik Süresi (gün)"
                type="number"
                value={localSettings.storageCleanupDays}
                onChange={(e) => handleSettingChange('storageCleanupDays', parseInt(e.target.value) || 30)}
                helperText="Geçici dosyaların silinme süresi"
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="İzinli Dosya Türleri"
                value={localSettings.allowedFileTypes}
                onChange={(e) => handleSettingChange('allowedFileTypes', e.target.value)}
                helperText="Virgül ile ayrılmış dosya uzantıları (örn: jpg,png,pdf)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.enableFileCompression}
                    onChange={(e) => handleSettingChange('enableFileCompression', e.target.checked)}
                  />
                }
                label="Otomatik Dosya Sıkıştırma"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => handleCleanup('temp')}
            >
              Geçici Dosyaları Temizle
            </Button>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => handleCleanup('logs')}
            >
              Eski Logları Temizle
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Backup Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BackupIcon />
            Yedekleme Ayarları
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.enableAutoBackup}
                onChange={(e) => handleSettingChange('enableAutoBackup', e.target.checked)}
              />
            }
            label="Otomatik yedekleme etkinleştir"
            sx={{ mb: 3 }}
          />

          {localSettings.enableAutoBackup && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Yedekleme Sıklığı</InputLabel>
                  <Select
                    value={localSettings.backupFrequency}
                    label="Yedekleme Sıklığı"
                    onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                  >
                    <MenuItem value="hourly">Saatlik</MenuItem>
                    <MenuItem value="daily">Günlük</MenuItem>
                    <MenuItem value="weekly">Haftalık</MenuItem>
                    <MenuItem value="monthly">Aylık</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Yedek Saklama Süresi (gün)"
                  type="number"
                  value={localSettings.backupRetentionDays}
                  onChange={(e) => handleSettingChange('backupRetentionDays', parseInt(e.target.value) || 30)}
                  helperText="Yedeklerin silinmeden önce saklanma süresi"
                  inputProps={{ min: 7, max: 365 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Yedekleme Konumu"
                  value={localSettings.backupLocation}
                  onChange={(e) => handleSettingChange('backupLocation', e.target.value)}
                  helperText="Yedeklerin kaydedileceği dizin yolu"
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Health Monitoring */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon />
            Sağlık İzleme
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.enableHealthChecks}
                onChange={(e) => handleSettingChange('enableHealthChecks', e.target.checked)}
              />
            }
            label="Sağlık kontrollerini etkinleştir"
            sx={{ mb: 3 }}
          />

          {localSettings.enableHealthChecks && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kontrol Aralığı (saniye)"
                  type="number"
                  value={localSettings.healthCheckInterval}
                  onChange={(e) => handleSettingChange('healthCheckInterval', parseInt(e.target.value) || 60)}
                  helperText="Sağlık kontrolü yapma aralığı"
                  inputProps={{ min: 30, max: 3600 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Uyarı Eşikleri
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="CPU Eşiği (%)"
                  type="number"
                  value={localSettings.alertThresholds.cpu}
                  onChange={(e) => handleNestedSettingChange('alertThresholds', 'cpu', parseInt(e.target.value) || 80)}
                  inputProps={{ min: 50, max: 95 }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="RAM Eşiği (%)"
                  type="number"
                  value={localSettings.alertThresholds.memory}
                  onChange={(e) => handleNestedSettingChange('alertThresholds', 'memory', parseInt(e.target.value) || 85)}
                  inputProps={{ min: 50, max: 95 }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Disk Eşiği (%)"
                  type="number"
                  value={localSettings.alertThresholds.disk}
                  onChange={(e) => handleNestedSettingChange('alertThresholds', 'disk', parseInt(e.target.value) || 90)}
                  inputProps={{ min: 70, max: 95 }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Yanıt Süresi (ms)"
                  type="number"
                  value={localSettings.alertThresholds.responseTime}
                  onChange={(e) => handleNestedSettingChange('alertThresholds', 'responseTime', parseInt(e.target.value) || 2000)}
                  inputProps={{ min: 500, max: 10000 }}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Maintenance Mode */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon />
            Bakım Modu
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.enableMaintenanceMode}
                onChange={(e) => handleSettingChange('enableMaintenanceMode', e.target.checked)}
                color="warning"
              />
            }
            label="Bakım modunu etkinleştir"
            sx={{ mb: 3 }}
          />

          {localSettings.enableMaintenanceMode && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Uyarı:</strong> Bakım modu etkinleştirildiğinde normal kullanıcılar sisteme erişemeyecek.
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            label="Bakım Mesajı"
            value={localSettings.maintenanceMessage}
            onChange={(e) => handleSettingChange('maintenanceMessage', e.target.value)}
            helperText="Bakım modu sırasında kullanıcılara gösterilecek mesaj"
            multiline
            rows={3}
            sx={{ mb: 3 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={localSettings.autoRestartOnCrash}
                onChange={(e) => handleSettingChange('autoRestartOnCrash', e.target.checked)}
              />
            }
            label="Çökme durumunda otomatik yeniden başlat"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Maksimum Yeniden Başlatma Denemesi"
            type="number"
            value={localSettings.restartMaxAttempts}
            onChange={(e) => handleSettingChange('restartMaxAttempts', parseInt(e.target.value) || 3)}
            helperText="Çökme sonrası maksimum yeniden başlatma deneme sayısı"
            inputProps={{ min: 1, max: 10 }}
          />
        </CardContent>
      </Card>

      {/* System Warnings */}
      <Box sx={{ mb: 3 }}>
        {systemStats.cpu > 80 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Kritik:</strong> CPU kullanımı yüksek (%{systemStats.cpu}). Sistem performansı etkilenebilir.
            </Typography>
          </Alert>
        )}

        {systemStats.memory > 85 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Uyarı:</strong> RAM kullanımı yüksek (%{systemStats.memory}). Bellek temizliği gerekebilir.
            </Typography>
          </Alert>
        )}

        {services.some(s => s.status === 'stopped') && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Uyarı:</strong> Bazı kritik servisler durmuş durumda. Lütfen servis durumlarını kontrol edin.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Cleanup Confirmation Dialog */}
      <Dialog
        open={cleanupDialog.open}
        onClose={() => setCleanupDialog({ open: false, type: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Temizlik İşlemi Onayı</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography>
            {cleanupDialog.type === 'temp' 
              ? 'Tüm geçici dosyalar silinecek. Bu işlem geri alınamaz.'
              : 'Eski log dosyaları silinecek. Bu işlem geri alınamaz.'
            }
          </Typography>
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            Devam etmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setCleanupDialog({ open: false, type: null })}>
            İptal
          </Button>
          <Button 
            onClick={performCleanup}
            variant="contained"
            color="warning"
            startIcon={<DeleteIcon />}
          >
            Temizle
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SystemConfiguration